import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  OnInit,
  inject,
} from '@angular/core';
import { UntypedFormArray, UntypedFormGroup, UntypedFormControl, UntypedFormBuilder } from '@angular/forms';

import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  NEVER,
  Observable,
  Subscription,
  withLatestFrom,
} from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { LanguageService } from '@fcom/ui-translate';
import { ElementActions, ElementTypes, GaContext, SelectOption } from '@fcom/common';
import { TabTheme, TabLayoutType, RadioItemGroupOption } from '@fcom/ui-components';
import { GtmService } from '@fcom/common/gtm';
import { TypedFormGroup, TypedFormArray } from '@fcom/service-forms';

import { SortBy } from '../../interfaces';

@Component({
  selector: 'fin-flight-selection-sort-by',
  styleUrls: ['./flight-selection-sort-by.component.scss'],
  templateUrl: './flight-selection-sort-by.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FlightSelectionSortByComponent implements OnChanges, OnInit {
  private gtmService = inject(GtmService);
  private fb = inject(UntypedFormBuilder);
  private languageService = inject(LanguageService);

  @Input()
  currencyCode: string;

  @Input()
  showPoints = false;

  @Input()
  resultsAmount: number;

  @Input()
  bestFlightPrice: string;

  @Input()
  fastestFlightPrice: string;

  @Input()
  lowestPrice: string;

  @Input()
  selectedSortBy$: Observable<SortBy>;

  @Input()
  isMobile$: Observable<boolean> = NEVER;

  @Input()
  showBestFlightsTab = true;

  @Input()
  isNewSortStyle = false;

  @Output()
  sortByChange: EventEmitter<SortBy> = new EventEmitter();

  shouldShowTabSubtitle = false;
  modalOpen = false;
  sortingForm: TypedFormGroup<UntypedFormArray | UntypedFormGroup | UntypedFormControl>;
  sortingOptions: RadioItemGroupOption[];
  sortingSelectOptions$: Observable<SelectOption[]>;
  allSortingOptions: RadioItemGroupOption[] = [
    { value: SortBy.BEST, label: 'flightSorting.bestFlights.plural', descriptionText: 'flightSorting.tooltip.text' },
    { value: SortBy.PRICE, label: 'flightSorting.lowestPrice' },
    { value: SortBy.DURATION, label: 'flightSorting.fastest' },
  ];
  allSortingOptionsDesktopNewStyle: RadioItemGroupOption[] = [
    {
      value: SortBy.BEST,
      label: 'flightSorting.recommended.desktopLabel',
    },
    { value: SortBy.PRICE, label: 'flightSorting.price.desktopLabel' },
    { value: SortBy.DURATION, label: 'flightSorting.duration.desktopLabel' },
    { value: SortBy.DEPARTURE, label: 'flightSorting.departureTime.desktopLabel' },
  ];
  allSortingOptionsMobileNewStyle: RadioItemGroupOption[] = [
    {
      value: SortBy.BEST,
      label: 'flightSorting.recommended.mobileLabel',
      descriptionText: 'flightSorting.recommended.description',
    },
    {
      value: SortBy.PRICE,
      label: 'flightSorting.price.mobileLabel',
      descriptionText: 'flightSorting.price.description',
    },
    {
      value: SortBy.DEPARTURE,
      label: 'flightSorting.departureTime.mobileLabel',
      descriptionText: 'flightSorting.departureTime.description',
    },
    {
      value: SortBy.DURATION,
      label: 'flightSorting.duration.mobileLabel',
      descriptionText: 'flightSorting.duration.description',
    },
  ];
  subscriptions: Subscription = new Subscription();

  readonly SortBy = SortBy;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly TabLayoutType = TabLayoutType;
  readonly TabTheme = TabTheme;

  ngOnInit(): void {
    this.sortingForm = this.fb.group({
      flightSorting: this.fb.control(''),
    }) as TypedFormGroup<TypedFormArray<UntypedFormGroup> | UntypedFormControl>;

    if (this.showBestFlightsTab) {
      this.sortingOptions = this.allSortingOptions;
    } else {
      this.sortingOptions = this.allSortingOptions.filter((option) => {
        return option.value !== SortBy.BEST;
      });
    }

    this.sortingSelectOptions$ = this.languageService
      .translateMultiple(this.allSortingOptionsDesktopNewStyle.map(({ label }) => label))
      .pipe(
        map((translated) =>
          this.allSortingOptionsDesktopNewStyle.map(({ value }, i) => ({
            value,
            name: translated[i],
          }))
        )
      );

    this.subscriptions.add(
      combineLatest([this.isMobile$, this.selectedSortBy$])
        .pipe(
          filter(([isMobile]) => isMobile === true || this.isNewSortStyle),
          map(([_, selectedSortBy]) => {
            setTimeout(() => {
              this.sortingForm.setValue({
                flightSorting: selectedSortBy,
              });
            });
          }),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
        )
        .subscribe()
    );

    this.onSortChangeFromModal();
  }

  ngOnChanges(): void {
    this.shouldShowTabSubtitle = this.resultsAmount !== 0;
  }

  onSortChangeFromTabs(event: { index: number; data: unknown }): void {
    this.gtmService.trackElement(
      `booking-flow-sort-change-${event.data}`,
      GaContext.BOOKING_FLOW,
      ElementTypes.TAB,
      undefined,
      ElementActions.CLICK
    );
    this.sortByChange.emit(event.data as SortBy);
  }

  onSortChangeFromModal(): void {
    this.subscriptions.add(
      this.sortingForm.valueChanges
        .pipe(
          withLatestFrom(this.isMobile$),
          filter(([_value, isMobile], index) => (this.isNewSortStyle || isMobile ? index > 0 : true)),
          distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
        )
        .subscribe(([sort]) => {
          this.gtmService.trackElement(
            `booking-flow-sort-change-${sort.flightSorting}`,
            GaContext.BOOKING_FLOW,
            ElementTypes.MODAL,
            undefined,
            ElementActions.CLICK
          );
          this.sortByChange.emit(sort.flightSorting);
        })
    );
  }

  openSortingModal(): void {
    this.sortByClickGtm();
    this.modalOpen = true;
  }

  closeSortingModal(): void {
    this.modalOpen = false;
  }

  sortByClickGtm(): void {
    this.gtmService.trackElement(
      'booking-flow-sort-change-sort-by-button',
      GaContext.BOOKING_FLOW,
      ElementTypes.BUTTON,
      undefined,
      ElementActions.CLICK
    );
  }
}
